import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getGroupsBySearch, getVariantsBySearch, ProductGroupLoadingKeys } from '../../slices/productGroupSlice';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import { getStatusColor } from '../StatusSelect';
import { ACTIVE, enumInventoryPhasedStatus } from '../../consts/inventoryStatus';
import { useDebounce } from '../../helpers/useDebounce';
import { arrayHasItems } from '../../helpers/common';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { SelectDropdown } from '../SelectDropdown';
import { ConfrimModal } from '../ConfrimModal';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';

const Search = ({
    label,
    placeholder,
    searchValue,
    setSearchValue
}) => {
    return (
        <>
            <label htmlFor="component-search" className="block text-sm font-medium text-gray-700">
                {label}
            </label>
            <div className="relative mt-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                <input
                    type="text"
                    id="component-search"
                    className="block w-full border rounded-md border-gray-300 pl-10 py-2 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder={placeholder}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    autoFocus
                />
            </div>
        </>
    )
}

export const BulkEditModal = ({
    isOpen,
    onClose,
    products,
    onAddVariant,
    onAcceptActions,
    onAddGroup,
    isReplenishable,
    defaultSearchTerm
}) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('variants');
    const [searchTerm, setSearchTerm] = useState(defaultSearchTerm || '');
    const currentSearch = useDebounce(searchTerm, 500);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedReplenishable, setSelectedReplenishable] = useState(null);
    const [deleteProductModalOpened, setDeleteProductModalOpened] = useState(false);

    const {
        loading: groupLoading,
        loadingKeys: groupLoadingKeys,
        searchedVariants: searchedVariantsByGroup,
        searchedGroups
    } = useSelector((state) => state.productGroup);

    useEffect(() => {
        if (!isOpen) return;
        setSearchTerm('');
        setSelectedStatus(null);
        setSelectedReplenishable(null);
        setDeleteProductModalOpened(false);
        if (activeTab === 'variants') {
            dispatch(getVariantsBySearch({
                search: '',
                isReplenishable: isReplenishable
            }));
        }
        if (activeTab === 'groups') {
            dispatch(getGroupsBySearch({
                search: '',
            }));
        }
    }, [activeTab, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (activeTab === 'variants') {
            dispatch(getVariantsBySearch({
                search: currentSearch,
                isReplenishable: isReplenishable
            }));
        }
        if (activeTab === 'groups') {
            dispatch(getGroupsBySearch({
                search: currentSearch,
            }));
        }
    }, [currentSearch, isReplenishable, isOpen]);

    const searchedVariantsIsLoading = () => 
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getVariants);
    const searchedGroupsIsLoading = () =>
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getGroups);
    const addVariantToTheProductsIsLoading = () =>
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.addSourceSKUToProducts);
    const addGroupToTheProductsIsLoading = () =>
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.addGroupIdToProducts);
    const updateProductsIsLoading = () =>
        groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.updateProducts);

    const handleAddVariant = async (variant) => {
        await onAddVariant(variant);
        setSearchTerm('');
    };
    const handleAddGroup = async (group) => {
        await onAddGroup(group);
        setSearchTerm('');
    }
    const handleAcceptActions = async () => {
        if (!selectedStatus && !selectedReplenishable) {
            toast.error(`No selected actions.`, { position: "bottom-right" });
            return;
        }
        await onAcceptActions({
            status: selectedStatus,
            replenishable: selectedReplenishable,
        });
    }
    const handleOnDelete = async () => {
        await onAcceptActions({
            toDelete: true
        });
    }

    const handleClose = () => {
        onClose();
        setSearchTerm('');
    };

    // Handle Esc key press
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }
        if (!isOpen) {
            setActiveTab('variants');
        }
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen]);

    const replanishableProducts = (products || []).filter((product) => product.isReplenishable);
    const hasIsReplanishableProducts = arrayHasItems(replanishableProducts);

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    aria-hidden="true"
                    onClick={handleClose}
                ></div>
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                <div 
                    className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="text-sm font-bold mb-2">
                            Selected products: {(products || []).length}   
                        </div>
                        <div className="sm:flex sm:items-start">
                            <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
                                <div className="border-b border-gray-200 mb-4">
                                    <nav className="flex -mb-px">
                                        <button
                                            onClick={() => setActiveTab('variants')}
                                            className={`py-2 px-2 text-sm font-medium ${
                                                activeTab === 'variants'
                                                ? 'border-b-2 border-blue-500 text-blue-600'
                                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            } focus:outline-none`}
                                            aria-label="Show variants"
                                        >
                                            Source SKU
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('groups')}
                                            className={`ml-4 py-2 px-2 text-sm font-medium ${
                                                activeTab === 'groups'
                                                ? 'border-b-2 border-blue-500 text-blue-600'
                                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            } focus:outline-none`}
                                            aria-label="Show groups"
                                        >
                                            Groups
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('actions')}
                                            className={`ml-4 py-2 px-2 text-sm font-medium ${
                                                activeTab === 'actions'
                                                ? 'border-b-2 border-blue-500 text-blue-600'
                                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            } focus:outline-none`}
                                            aria-label="Show actions"
                                        >
                                            Actions
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('delete')}
                                            className={`ml-4 py-2 px-2 text-sm font-medium ${
                                                activeTab === 'delete'
                                                ? 'border-b-2 border-blue-500 text-blue-600'
                                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            } focus:outline-none`}
                                            aria-label="Show actions"
                                        >
                                            Delete
                                        </button>
                                    </nav>
                                </div>
                                {activeTab === 'variants' && (
                                    <div key={activeTab}>
                                        {hasIsReplanishableProducts && (
                                            <div className="flex gap-2 flex-row items-center mb-2">
                                                <InformationCircleIcon
                                                className={`w-full h-5 max-w-5 text-yellow-600`}
                                                />
                                                <p className="text-sm text-yellow-600">
                                                    Some selected products have "Is Replenishable" state.
                                                    <br />
                                                    Sku's: {`${replanishableProducts.map((product) => product.sku).slice(0, 10).join(", ")} ${replanishableProducts.length > 10 ? `... and ${replanishableProducts.length - 10} products` : ""}`}
                                                </p>
                                            </div>
                                        )}
                                        <h3 className="text-lg font-medium leading-6 text-gray-900">Add Source SKU</h3>
                                        <div className="mt-4">
                                            <div className="mb-4">
                                                <Search
                                                    label={"Search Variants"}
                                                    searchValue={searchTerm}
                                                    setSearchValue={setSearchTerm}
                                                    placeholder={"Search by name, SKU, or category"}
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-2 max-h-60 overflow-y-auto border rounded-md relative">
                                            {(searchedVariantsIsLoading() || addVariantToTheProductsIsLoading()) && <OverlayGrayBackground />}
                                            {searchedVariantsByGroup.length > 0 ? (
                                                <ul className="divide-y divide-gray-200">
                                                    {searchedVariantsByGroup.map((variant) => (
                                                    <li 
                                                        key={variant.id}
                                                        className="cursor-pointer px-4 py-3 hover:bg-gray-50"
                                                        onClick={async () => {
                                                            await handleAddVariant(variant)
                                                        }}
                                                        onKeyDown={async (e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                await handleAddVariant(variant);
                                                            }
                                                        }}
                                                        tabIndex={0}
                                                        role="button"
                                                        aria-label={`Add component ${variant.name}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{variant.name}</p>
                                                                <p className="text-sm text-gray-500">SKU: {variant.sku}</p>
                                                            </div>
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(variant.status)}`}>
                                                                {enumInventoryPhasedStatus[variant.status]}
                                                            </span>
                                                        </div>
                                                    </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <div className="flex items-center justify-center py-8">
                                                    <p className="text-gray-500">No variants found</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'groups' && (
                                    <div key={activeTab}>
                                        <h3 className="text-lg font-medium leading-6 text-gray-900">Add To The Group</h3>
                                        <div className="mt-4">
                                            <div className="mb-4">
                                                <Search
                                                    label={"Search Groups"}
                                                    searchValue={searchTerm}
                                                    setSearchValue={setSearchTerm}
                                                    placeholder={"Search by name"}
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-2 max-h-60 overflow-y-auto border rounded-md relative">
                                            {(searchedGroupsIsLoading() || addGroupToTheProductsIsLoading()) && <OverlayGrayBackground />}
                                            {searchedGroups.length > 0 ? (
                                                <ul className="divide-y divide-gray-200">
                                                    {searchedGroups.map((group) => (
                                                        <li 
                                                            key={group.id}
                                                            className="cursor-pointer px-4 py-3 hover:bg-gray-50"
                                                            onClick={async () => {
                                                                await handleAddGroup(group)
                                                            }}
                                                            onKeyDown={async (e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    await handleAddGroup(group);
                                                                }
                                                            }}
                                                            tabIndex={0}
                                                            role="button"
                                                            aria-label={`Add component ${group.name}`}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">{group.name}</p>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <div className="flex items-center justify-center py-8">
                                                    <p className="text-gray-500">No groups found</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'actions' && (
                                    <div key={activeTab} className="flex flex-col gap-2 mb-4 relative">
                                        {(updateProductsIsLoading()) && <OverlayGrayBackground />}
                                        <div className="flex flex-col gap-1">
                                            <span>Status:</span>
                                            <div className="flex-1">
                                                <SelectDropdown
                                                    options={[
                                                        { label: "Active", value: "ACTIVE" }, 
                                                        { label: "Inactive", value: "IN_ACTIVE" },
                                                        { label: "Sunset", value: "SUNSET" },
                                                        { label: "Planned", value: "PLANNED" }
                                                    ]}
                                                    value={selectedStatus}
                                                    onChange={(val) => {
                                                        const next = typeof val === 'string' ? val : val?.target?.value;
                                                        setSelectedStatus(next);
                                                        if (next === 'IN_ACTIVE' || next === 'SUNSET' || next === 'PLANNED') {
                                                            setSelectedReplenishable('false');
                                                        }
                                                    }}
                                                    placeholder=""
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2">
                                                <span>Replenishable:</span>
                                                <InformationCircleIcon
                                                    className="h-5 w-5 text-blue-500"
                                                    data-tip
                                                    data-for="repl-info-tooltip"
                                                />
                                                <ReactTooltip id="repl-info-tooltip" effect="solid" place="top" className="max-w-none">
                                                    <div style={{ width: 170, whiteSpace: 'normal' }}>
                                                        Is Replenishable option will be accessible only with status ACTIVE selected.
                                                    </div>
                                                </ReactTooltip>
                                            </div>
                                            <div className="flex-1">
                                                <SelectDropdown
                                                    options={[
                                                        selectedStatus === ACTIVE ? { label: "Is Replenishable", value: "true" } : null, 
                                                        { label: "Non Replenishable", value: "false" }
                                                    ].filter(Boolean)}
                                                    value={selectedReplenishable}
                                                    onChange={setSelectedReplenishable}
                                                    placeholder=""
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'delete' && (
                                    <div key={activeTab} className="flex flex-col gap-2 mb-4 relative items-center">
                                        <button 
                                            className='inline-flex items-center w-52 justify-center px-3 py-1.5 text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded border border-black disabled:opacity-50'
                                            onClick={() => setDeleteProductModalOpened(true)}
                                            disabled={updateProductsIsLoading()}
                                        >
                                            Delete Products
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {activeTab !== 'delete' && (
                            <div className="py-3 sm:flex sm:flex-row-reverse">
                                {activeTab === "actions" && (
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={handleAcceptActions}
                                    >
                                        Accept
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ConfrimModal
                isOpen={deleteProductModalOpened}
                title={"Are you sure want to delete these products?"}
                confirmButtonTitle={"Delete"}
                isLoading={updateProductsIsLoading()}
                submitClick={async () => {
                    setDeleteProductModalOpened(false)
                    await handleOnDelete();
                }}
                cancelClick={() => {
                    setDeleteProductModalOpened(false)
                }}
            />
        </div>
    );

    return createPortal(modalContent, document.body);
};